import { ApolloClient } from '@apollo/client'
import { NormalizedCacheObject } from '@apollo/client/cache'
import { User } from 'oidc-client-ts'
import { Auth0User, Roles } from '../types'
import { CREATE_USERS, GET_SELF } from '../graphql/authentication'
import {
  AdminUserPermission,
  AppUser,
  CreateUsersMutationResult,
  CreateUsersMutationVariables
} from '../generated/graphql'

/**
 * Post login student-app needs to kick off user creation
 * depending on one or many roles. This should be a UI / UX
 * blocking action.
 *
 * TODO: delete sub
 * @param  {ApolloClient<NormalizedCacheObject>} client
 * @param  {Auth0User} userProfile
 * returns createUserResponse with appUserId and permissions
 */
export async function createUsers(
  client: ApolloClient<NormalizedCacheObject>,
  userProfile: Auth0User
) {
  const createUsersResult = await client.mutate<
    CreateUsersMutationResult,
    CreateUsersMutationVariables
  >({
    mutation: CREATE_USERS,
    variables: {
      input: {
        sub: userProfile.sub,
        picture: userProfile.picture
      }
    }
  })

  // A confusing one, the actual data structure is createUsersResult!.data!.success!
  // but typescript thinks it should be createUsersResult!.data!.data!.success!
  // @ts-ignore
  return createUsersResult.data && createUsersResult!.data!.createUsers.appUserId
    ? // @ts-ignore
      createUsersResult!.data!.createUsers
    : 0
}

export async function createUsersOIDC(client: ApolloClient<NormalizedCacheObject>, oidcUser: User) {
  // Directly use the 'sub' from OIDC User and handle the 'picture' if available
  const { sub, picture } = oidcUser.profile

  const createUsersResult = await client.mutate<
    CreateUsersMutationResult,
    CreateUsersMutationVariables
  >({
    mutation: CREATE_USERS,
    variables: {
      input: {
        sub,
        picture
      }
    }
  })

  // A confusing one, the actual data structure is createUsersResult!.data!.success!
  // but typescript thinks it should be createUsersResult!.data!.data!.success!
  // @ts-ignore
  return createUsersResult.data && createUsersResult!.data!.createUsers.appUserId
    ? // @ts-ignore
      createUsersResult!.data!.createUsers
    : 0
}

export const extractRolesFromOIDCUser = (oidcUser: User): Roles[] => {
  // Assuming roles are stored under a custom namespace
  const rolesPath = 'https://api.student.civitaslearning.com/roles'
  const rawRoles = oidcUser.profile[rolesPath]
  if (!Array.isArray(rawRoles)) {
    return []
  }
  return rawRoles
    .map((role) => {
      // Validate if the role is part of the enum, otherwise ignore or handle the error
      if (Object.values(Roles).includes(role as Roles)) {
        return role as Roles
      }
      return null
    })
    .filter((role) => role !== null) as Roles[] // Filter out nulls, ensure all are valid Roles
}

export const mapOIDCToAuth0User = (oidcUser: User): Auth0User => ({
  sub: oidcUser.profile.sub as string,
  firstName: oidcUser.profile['https://api.student.civitaslearning.com/firstName'] as string,
  lastName: oidcUser.profile['https://api.student.civitaslearning.com/lastName'] as string,
  email: oidcUser.profile['https://api.student.civitaslearning.com/email'] as string,
  picture: oidcUser.profile.picture as string,
  roles: extractRolesFromOIDCUser(oidcUser),
  'https://api.student.civitaslearning.com/roles': extractRolesFromOIDCUser(oidcUser),
  // Forward the SIS identity claims so persona guards (e.g. blocking an advisor
  // from opening their own student record) can recognise the current user
  // before any request runs. Without these the claims are dropped and the
  // pre-mutation self-record check silently no-ops in production.
  'https://api.student.civitaslearning.com/studentId': oidcUser.profile[
    'https://api.student.civitaslearning.com/studentId'
  ] as string | undefined,
  'https://api.student.civitaslearning.com/externalId': oidcUser.profile[
    'https://api.student.civitaslearning.com/externalId'
  ] as string | undefined,
  permissions: (oidcUser.profile.permissions as AdminUserPermission[]) || []
})

export function userIsAdmin(user: Auth0User | undefined): boolean {
  if (!user) {
    return false
  }
  return userHasRole(user, [Roles.admin])
}

/** Specifically for student only.  Excludes civitas and dual role admin users */
export function userIsOnlyStudent(user: Auth0User | undefined): boolean {
  if (!user) {
    return false
  }
  if (
    user &&
    user.roles &&
    user.roles.includes(Roles.student) &&
    !user.roles.includes(Roles.admin)
  ) {
    return true
  }
  return false
}

/** Check if a user is an admin, excluding civitas users */
export function userIsOnlyAdmin(user: Auth0User | undefined): boolean {
  if (!user || !user.roles) {
    return false
  }
  if (user.roles.includes(Roles.admin)) {
    return true
  }
  return false
}

export function userHasRole(user: Auth0User | undefined, roles: Array<Roles>): boolean {
  if (
    user &&
    user.roles &&
    (user.roles.includes(Roles.civitasEmployee) || roles.some((r) => user?.roles?.includes(r)))
  ) {
    return true
  }
  return false
}

/**
 * Check if the current user has the permission(s) provided.
 * @param user Currently logged in user
 * @param permissions One or more permissions
 * @returns boolean indicating if the user has the permission
 * If a list of permissions is provided, access is granted if the user
 * has at least one permission.  Access to all permissions is not required.
 */
export function userHasPermission(
  user: Auth0User | undefined,
  permissions: AdminUserPermission | Array<AdminUserPermission> | undefined
): boolean {
  if (user?.roles?.includes(Roles.civitasEmployee)) {
    return true
  }

  if (!user || !user.permissions || !permissions) {
    return false
  }
  if (Array.isArray(permissions)) {
    return permissions.some((p) => user.permissions?.includes(p))
  }
  return user.permissions.includes(permissions)
}

export function userCanLoginToScheduler(
  user: Auth0User | undefined,
  planOwnerAppUserId: number
): boolean {
  const isStudent = user?.roles?.includes(Roles.student)
  const isPlanOwner = planOwnerAppUserId === user?.appUserId

  // This will return true if the user is a civitas employee or they have sampleStudentLogin permission
  const hasSampleStudentLoginPermission = userHasPermission(user, [
    AdminUserPermission.SampleStudentLogin
  ])

  return (isStudent && isPlanOwner) || hasSampleStudentLoginPermission
}

export async function getSelf(client: ApolloClient<NormalizedCacheObject>): Promise<AppUser> {
  const self = await client.query({
    query: GET_SELF
  })
  return self.data.self
}

/**
 * Check if the current user has the read-only plans permission.
 * @param user Currently logged in user
 * @returns boolean indicating if the user has the read-only plans permission
 */
export function userHasReadonlyPermission(user: Auth0User | undefined): boolean {
  return (
    userHasPermission(user, AdminUserPermission.ReadonlyPlans) &&
    !user?.roles?.includes(Roles.civitasEmployee)
  )
}
